/**
 * Modal subtitle picker.
 *
 * Replaces the inline upward-popping menu — that was hard to read once you
 * had more than 5 entries, and limited to one option per language. This
 * modal shows EVERY OpenSubtitles result grouped by language, with a search
 * filter, multiple alternatives per language with their release names + flags,
 * plus any embedded subtitles. User explicitly picks one; nothing is loaded
 * automatically.
 */
import { useMemo, useState } from "react";
import { Search, X } from "lucide-react";
import { Subs, type OpenSubtitleResult } from "../lib/api";
import type { PlaybackSubtitle } from "../contexts/PlaybackContext";

interface EmbeddedTrack {
  url: string;
  lang: string;
  label: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  results: OpenSubtitleResult[];
  embedded: EmbeddedTrack[];
  currentUrl: string | null;
  onPick: (sub: PlaybackSubtitle | null) => void;
}

let _langDisplay: { of(s: string): string | undefined } | null = null;
function friendlyLang(code: string): string {
  if (!code || code === "und") return "Unknown";
  if (!_langDisplay) {
    try {
      _langDisplay = new Intl.DisplayNames([navigator.language || "en"], { type: "language" });
    } catch { _langDisplay = null; }
  }
  try {
    const v = _langDisplay?.of(code);
    if (v && v !== code) return v;
  } catch { /* ignore */ }
  return code.toUpperCase();
}

function fmtDownloads(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

export function SubtitleBrowser({ open, onClose, results, embedded, currentUrl, onPick }: Props) {
  const [filter, setFilter] = useState("");

  // Group OpenSubtitles results by language; sort entries within each language
  // by download count, and languages by total downloads.
  const grouped = useMemo(() => {
    const m = new Map<string, OpenSubtitleResult[]>();
    for (const r of results) {
      if (!r.language || !r.file_id) continue;
      const arr = m.get(r.language);
      if (arr) arr.push(r);
      else m.set(r.language, [r]);
    }
    for (const arr of m.values()) {
      arr.sort((a, b) => (b.downloads || 0) - (a.downloads || 0));
    }
    return Array.from(m.entries()).sort((a, b) => {
      const da = a[1].reduce((s, x) => s + (x.downloads || 0), 0);
      const db = b[1].reduce((s, x) => s + (x.downloads || 0), 0);
      return db - da;
    });
  }, [results]);

  const filterLower = filter.toLowerCase().trim();
  const matchesLang = (lang: string) =>
    !filterLower ||
    lang.toLowerCase().includes(filterLower) ||
    friendlyLang(lang).toLowerCase().includes(filterLower);
  const filtered = grouped.filter(([lang]) => matchesLang(lang));
  const filteredEmbedded = embedded.filter((t) => matchesLang(t.lang));

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] bg-canvas/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-[640px] max-h-[85vh] glass-panel rounded-xl flex flex-col overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-muted/30">
          <div>
            <h2 className="font-display text-[24px] tracking-[-0.5px] text-text-primary">Subtitles</h2>
            <p className="text-[11px] font-ui text-text-secondary mt-0.5">
              {results.length} from OpenSubtitles{embedded.length ? ` · ${embedded.length} embedded` : ""}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-text-secondary hover:text-text-primary p-1"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        {/* Search */}
        <div className="p-3 border-b border-muted/30">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary pointer-events-none" />
            <input
              autoFocus
              type="search"
              placeholder="Filter by language (e.g. English, Dutch, fre)…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="w-full pl-9 pr-3 py-2 rounded-lg bg-surface border border-muted/30 text-[13px] font-ui text-text-primary placeholder:text-text-secondary focus:outline-none focus:border-accent-gold/50"
            />
          </div>
        </div>

        {/* Off button */}
        <div className="px-3 pt-3">
          <button
            onClick={() => { onPick(null); onClose(); }}
            className={`w-full text-left px-3 py-2 rounded-lg text-[13px] font-ui transition-fast ${
              !currentUrl
                ? "bg-accent-gold/20 text-accent-gold"
                : "text-text-primary hover:bg-card-hover border border-muted/20"
            }`}
          >
            Off
          </button>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto p-3 space-y-4">
          {filteredEmbedded.length > 0 && (
            <section>
              <p className="text-[10px] font-mono uppercase tracking-[0.06em] text-accent-gold/80 px-1 mb-2">
                Embedded in source
              </p>
              <div className="space-y-1">
                {filteredEmbedded.map((t) => (
                  <button
                    key={t.url}
                    onClick={() => { onPick({ url: t.url, lang: t.lang, label: t.label }); onClose(); }}
                    className={`w-full text-left px-3 py-2 rounded-lg text-[12px] font-ui transition-fast border ${
                      currentUrl === t.url
                        ? "bg-accent-gold/20 text-accent-gold border-accent-gold/40"
                        : "text-text-primary hover:bg-card-hover border-muted/20"
                    }`}
                  >
                    <div className="truncate">{t.label}</div>
                    <div className="text-[10px] text-text-secondary mt-0.5">
                      Slow to load — extracted from the source file
                    </div>
                  </button>
                ))}
              </div>
            </section>
          )}

          {filtered.map(([lang, entries]) => (
            <section key={lang}>
              <p className="text-[10px] font-mono uppercase tracking-[0.06em] text-accent-gold/80 px-1 mb-2 flex items-center gap-2">
                {friendlyLang(lang)}
                <span className="text-text-secondary normal-case tracking-normal">
                  · {entries.length} option{entries.length > 1 ? "s" : ""}
                </span>
              </p>
              <div className="space-y-1">
                {entries.map((e) => {
                  const url = Subs.url(e.file_id);
                  const flags: string[] = [];
                  if (e.hd) flags.push("HD");
                  if (e.from_trusted) flags.push("Trusted");
                  if (e.machine_translated || e.ai_translated) flags.push("AI-translated");
                  const isActive = currentUrl === url;
                  return (
                    <button
                      key={e.file_id}
                      onClick={() => {
                        onPick({
                          url,
                          lang: e.language,
                          label: `${friendlyLang(e.language)} · ${e.release || "Untitled"}`,
                        });
                        onClose();
                      }}
                      className={`w-full text-left px-3 py-2 rounded-lg text-[12px] font-ui transition-fast border ${
                        isActive
                          ? "bg-accent-gold/20 text-accent-gold border-accent-gold/40"
                          : "text-text-primary hover:bg-card-hover border-muted/20"
                      }`}
                    >
                      <div className="truncate">{e.release || "Untitled"}</div>
                      <div className="text-[10px] text-text-secondary mt-1 flex items-center gap-1.5 flex-wrap">
                        <span className="tabular-nums">{fmtDownloads(e.downloads)} downloads</span>
                        {e.uploader && (
                          <span className="text-text-secondary/70">· by {e.uploader}</span>
                        )}
                        {flags.map((f) => (
                          <span
                            key={f}
                            className={`px-1.5 py-0.5 rounded text-[9px] font-medium uppercase tracking-[0.04em] ${
                              f === "AI-translated"
                                ? "bg-danger/20 text-danger"
                                : f === "Trusted"
                                  ? "bg-accent-gold/20 text-accent-gold"
                                  : "bg-muted/30 text-text-secondary"
                            }`}
                          >
                            {f}
                          </span>
                        ))}
                      </div>
                    </button>
                  );
                })}
              </div>
            </section>
          ))}

          {filtered.length === 0 && filteredEmbedded.length === 0 && !filterLower && (
            <p className="text-center text-text-secondary text-[12px] font-ui py-8">
              No subtitles available for this title.
            </p>
          )}
          {filtered.length === 0 && filteredEmbedded.length === 0 && filterLower && (
            <p className="text-center text-text-secondary text-[12px] font-ui py-4">
              No matches for "{filter}".
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
